import { useMemo, useState } from 'react'
import { useStores } from '../app/StoreContext'
import { Button, plural } from './primitives'

/**
 * Selettore del punto vendita nella barra in alto: un filtro di vista sui punti vendita
 * autorizzati, non una scelta di postazione. L'ambito vero lo impone il server; chi ha un solo
 * punto vendita vede quello e nessun comando.
 */
export function StorePicker() {
  const { stores, selected, toggle, select, loading, storeName } = useStores()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return stores
    return stores.filter((s) => s.code.toLowerCase().includes(q) || s.name.toLowerCase().includes(q))
  }, [stores, search])

  const label =
    selected.length === 0 ? 'Tutti i punti vendita'
    : selected.length === 1 ? `${selected[0]} — ${storeName(selected[0])}`
    : plural(selected.length, 'punto vendita', 'punti vendita')

  // Spunta o toglie in blocco. Agisce su ciò che è a video: con una ricerca attiva riguarda
  // solo i punti vendita trovati, che è il gesto utile — «prendi tutti i romani» — mentre
  // spuntare l'intera anagrafica per poi togliere il resto non lo sarebbe.
  const searching = search.trim().length > 0
  const allShownSelected = filtered.length > 0 && filtered.every((s) => selected.includes(s.code))

  const toggleShown = () => {
    const shown = filtered.map((s) => s.code)
    select(
      allShownSelected ? selected.filter((c) => !shown.includes(c))
      : [...new Set([...selected, ...shown])],
    )
  }

  // Con un solo punto vendita autorizzato non c'è nulla da filtrare: si mostra quello,
  // non un comando che non farebbe niente.
  if (!loading && stores.length === 1) {
    return (
      <div className="store-picker">
        <span className="store-picker-fixed" title="Il tuo punto vendita">
          {`${stores[0].code} — ${stores[0].name}`}
        </span>
      </div>
    )
  }

  return (
    <div className="store-picker">
      <button className="store-picker-trigger" onClick={() => setOpen((o) => !o)} disabled={loading}>
        <span className="store-picker-icon" aria-hidden>▾</span>
        <span className="store-picker-label">{loading ? 'Caricamento…' : label}</span>
      </button>

      {open && (
        <>
          <div className="overlay" onClick={() => setOpen(false)} />
          <div className="store-picker-panel">
            <input
              className="store-picker-search"
              placeholder="Cerca per codice o nome"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />

            <div className="store-picker-tools">
              <button
                type="button"
                className="store-picker-bulk"
                onClick={toggleShown}
                disabled={filtered.length === 0}
              >
                {allShownSelected ? 'Deseleziona' : 'Seleziona'}{' '}
                {searching ? `i ${filtered.length} trovati` : 'tutti'}
              </button>

              {/* Con nessuna spunta l'applicazione mostra comunque tutto: senza dirlo,
                  una selezione vuota sembrerebbe un filtro che nasconde i dati. */}
              <span className="store-picker-count muted">
                {selected.length === 0 ?
                  'nessuno selezionato: valgono tutti'
                : `${selected.length} di ${stores.length} selezionati`}
              </span>
            </div>

            <div className="store-picker-list">
              {filtered.map((s) => (
                <label key={s.code} className="store-picker-item">
                  <input type="checkbox" checked={selected.includes(s.code)} onChange={() => toggle(s.code)} />
                  <span className="store-code">{s.code}</span>
                  <span className="store-name">{s.name}</span>
                </label>
              ))}
              {filtered.length === 0 && <div className="empty">Nessun punto vendita trovato.</div>}
            </div>

            <div className="store-picker-foot">
              {/* Azzera la selezione, che per l'applicazione significa «nessun filtro, quindi
                  tutti». Detto «Tutti» accanto a «Seleziona tutti» sembrerebbe lo stesso
                  comando: il verbo distingue il togliere il filtro dallo spuntare le voci. */}
              <Button
                variant="ghost"
                onClick={() => select([])}
                title="Toglie il filtro: le schermate mostrano tutti i punti vendita"
              >
                Mostra tutti
              </Button>
              <Button variant="primary" onClick={() => setOpen(false)}>
                Fatto
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
